import { clamp01, smoothstep } from '@/core/math/scalar';
import type { Spacecraft } from './Spacecraft';

/**
 * Ceiling on main-engine throttle imposed by dynamic pressure and hull heating.
 *
 * This is the limiter `LaunchToOrbitMacro` has always flown its ascent with —
 * exactly the same thresholds, lifted here unchanged so manual flight gets it
 * too. Climbing out of an atmosphere by hand otherwise means watching the q and
 * temperature readouts and throttling back on them yourself, which is the one
 * part of an ascent that is pure bookkeeping rather than piloting.
 *
 * It is a *ceiling*, not a multiplier, so applying it twice is the same as
 * applying it once: the macro sets a governed throttle, the flight computer
 * then takes the minimum of that and the same ceiling, and the ascent flies
 * exactly as it did before.
 */
export interface ThrottleLimit {
  /** Highest throttle the vessel may be commanded to, 0..1. */
  limit: number;
  /** Which limit is biting, for the pilot's benefit. Null when neither is. */
  reason: string | null;
}

/**
 * Default safety margin. Matches `MacroSafety.margin`'s own default, so a
 * hand-flown climb and an autopiloted one back off at the same point.
 */
export const DEFAULT_GOVERNOR_MARGIN = 0.35;

export function throttleGovernor(ship: Spacecraft, margin = DEFAULT_GOVERNOR_MARGIN): ThrottleLimit {
  const q = ship.lastAero?.dynamicPressure ?? 0;
  const qLimit = ship.def.maxDynamicPressure * (1 - margin * 0.35);
  const aero = clamp01(1 - smoothstep(qLimit * 0.75, qLimit, q));

  const thermalRatio = ship.thermal.temperature / Math.max(1, ship.thermal.temperatureLimit);
  const thermal = clamp01(1 - smoothstep(0.8, 0.98, thermalRatio));

  return {
    limit: aero * thermal,
    // Report whichever is cutting harder; a climb that is both hot and fast is
    // being flown too steeply for the air, which is the aerodynamic message.
    reason: aero >= 1 && thermal >= 1 ? null : aero <= thermal ? 'Dynamic pressure' : 'Hull temperature',
  };
}
